import { Form, Input, Button, Card, Typography, App as AntApp } from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLoginMutation, useMe } from "@/hooks/useAuth";
import { getNormalized } from "@/api/client";

interface LoginFormValues {
  email: string;
  password: string;
}

interface LocationState {
  returnTo?: string;
}

export function LoginPage() {
  const [form] = Form.useForm<LoginFormValues>();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLoginMutation();
  const { data: me } = useMe();

  const returnTo = (location.state as LocationState | null)?.returnTo ?? "/";

  // Если уже залогинен — сразу редирект.
  useEffect(() => {
    if (me) navigate(returnTo, { replace: true });
  }, [me, navigate, returnTo]);

  const onFinish = async (values: LoginFormValues) => {
    try {
      await loginMutation.mutateAsync(values);
      message.success("Вы вошли в систему");
      navigate(returnTo, { replace: true });
    } catch (err) {
      const normalized = getNormalized(err);
      // Для login страницы НЕ показываем глобальный toast (он уже показал общий),
      // вместо этого даём специфичные подсказки:
      if (normalized.status === 423) {
        const secs = normalized.retry_after_seconds;
        message.error(
          secs
            ? `Аккаунт временно заблокирован, попробуйте через ~${Math.ceil(secs / 60)} мин`
            : "Аккаунт временно заблокирован",
        );
      } else if (normalized.status === 400 || normalized.status === 401) {
        message.error("Неверный email или пароль");
      }
      // Прочие ошибки уже показал GlobalErrorListener.
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: 24,
      }}
    >
      <Card style={{ width: "100%", maxWidth: 380 }}>
        <Typography.Title level={3} style={{ marginTop: 0, textAlign: "center" }}>
          Вход в Multitool
        </Typography.Title>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          scrollToFirstError={{ focus: true }}
          autoComplete="on"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Введите email" },
              { type: "email", message: "Некорректный email" },
              { max: 254, message: "Слишком длинный email" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            label="Пароль"
            name="password"
            rules={[
              { required: true, message: "Введите пароль" },
              { max: 128, message: "Слишком длинный пароль" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              autoComplete="current-password"
              placeholder="Пароль"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loginMutation.isPending}
            >
              Войти
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
